import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Layout,
  message,
  Row,
  Select,
  Radio,
  Upload,
} from "antd";
import { useWatch } from "antd/es/form/Form";
import { Trash, Plus, Inbox, TriangleAlert, CheckCircle, XCircle } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import ConfirmationModal from "../../Components/CustomModals/ConfirmationModal";
import {
  formatNumbers,
  formatString,
  navigateTo,
} from "../../Utils/Common";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { useParams } from "react-router-dom";

const { Dragger } = Upload;

interface PurchaseRequestFormProps {
  add?: boolean;
  edit?: boolean;
  view?: boolean;
  approval?: boolean;
}

const PurchaseRequestForm: React.FC<PurchaseRequestFormProps> = ({
  add,
  edit,
  view,
  approval,
}) => {
  const [loading,      setLoading]      = useState("");
  const [showModal,    setShowModal]    = useState("");
  const [form]                          = Form.useForm();
  const [expenseTypes, setExpenseTypes] = useState<any[]>([]);
  const [branches,     setBranches]     = useState<any[]>([]);
  const [suppliers,    setSuppliers]    = useState<any[]>([]);
  const [items,        setItems]        = useState<any[]>([]);
  const [disapprovalReason, setDisapprovalReason] = useState("");
  const [withholdingTaxPercent, setWithholdingTaxPercent] = useState(0);

  const itemsArray = useWatch("itemsArray", form);
  const { id: purchase_request_id } = useParams<{ id: string }>();

  function clearFields() {
    form.resetFields();
  }

  // ─── Totals ───────────────────────────────────────────────────────────────────
  const subTotal = (itemsArray || []).reduce((sum: number, item: any) => {
    return sum + (Number(item?.qty) || 0) * (Number(item?.price) || 0);
  }, 0);
  const withholdingTaxAmount = subTotal * (withholdingTaxPercent / 100);
  const grandTotal           = subTotal - withholdingTaxAmount;

  // ─── Upload ───────────────────────────────────────────────────────────────────
  const handleBeforeUpload = (file: any) => {
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) message.error(`${file.name} exceeds the 2MB size limit.`);
    return isLt2M ? false : Upload.LIST_IGNORE;
  };

  // ─── Submit (add / edit) ──────────────────────────────────────────────────────
  async function submitForm(data: any) {
    const formattedItemsArray = (data.itemsArray || []).map((item: any) => ({
      ...item,
      total_amount: parseFloat(
        ((Number(item.qty) || 0) * (Number(item.price) || 0)).toFixed(2)
      ),
    }));

    const formattedData = {
      ...data,
      remarks:        data.remarks ?? "",
      expense_date:   data.expense_date ? dayjs(data.expense_date).format("YYYY-MM-DD") : "",
      itemsArray:     formattedItemsArray,
      grand_total:    grandTotal,
      wht_percentage: withholdingTaxPercent,
      wht_amount:     withholdingTaxAmount,
    };

    if (edit) formattedData.purchase_request_id = purchase_request_id;

    const formData = new FormData();
    for (const key in formattedData) {
      if (key === "itemsArray") {
        formData.append(key, JSON.stringify(formattedData[key]));
      } else if (key === "attachment") {
        (formattedData[key] || []).forEach((file: any) => {
          if (file.originFileObj) formData.append("attachments[]", file.originFileObj);
        });
      } else {
        const value = formattedData[key];
        if (value !== null && value !== undefined) formData.append(key, value);
      }
    }

    setLoading("submit");
    const endpoint = add ? "purchase_requests/create" : "purchase_requests/update";
    const response = await postRequest(endpoint, formData, true);

    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setShowModal("");
        setLoading("");
        navigateTo("/purchase-request");
      }, 1000);
    } else {
      setLoading("");
      setShowModal("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${add ? "create" : "update"} purchase request.`
      );
    }
  }

  // ─── Approve ──────────────────────────────────────────────────────────────────
  async function approveRequest() {
    setLoading("approve");
    const response = await postRequest(
      `purchase_requests/change_status`,
      { purchase_request_id, action: "approved" },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setShowModal("");
        setLoading("");
        navigateTo("/purchase-request");
      }, 1000);
    } else {
      setLoading("");
      setShowModal("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to approve purchase request."
      );
    }
  }

  // ─── Disapprove ───────────────────────────────────────────────────────────────
  async function disapproveRequest() {
    if (!disapprovalReason.trim()) {
      message.warning("Please provide a reason for disapproval.");
      return;
    }
    setLoading("disapprove");
    const response = await postRequest(
      `purchase_requests/change_status`,
      { purchase_request_id, action: "disapproved", reason: disapprovalReason },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setShowModal("");
        setLoading("");
        navigateTo("/purchase-request");
      }, 1000);
    } else {
      setLoading("");
      setShowModal("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to disapprove purchase request."
      );
    }
  }

  // ─── Fetch helpers ────────────────────────────────────────────────────────────
  async function fetchBranches() {
    const response = await getRequest(`branches/get`, true);
    if (response.success) setBranches(response.data.data);
  }

  async function fetchExpenseTypes() {
    const response = await getRequest(`expense_types/get`, true);
    if (response.success) setExpenseTypes(response.data.data);
  }

  async function fetchSuppliers() {
    const response = await getRequest(`suppliers/get`, true);
    if (response.success) setSuppliers(response.data.data);
  }

  async function fetchItems() {
    const response = await getRequest(`items/get`, true);
    if (response.success) setItems(response.data.data);
  }

  // ─── Effects ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if ((edit || view || approval) && expenseTypes.length > 0) {
      setLoading("fetch");
      getRequest(`purchase_requests/get`, { purchase_request_id }, true).then((response) => {
        if (response.success) {
          const data = response.data.data[0];
          form.setFieldsValue({
            supplier_name:  data.supplier_name,
            expense_date:   data.expense_date ? dayjs(data.expense_date) : null,
            branch_id:      Number(data.branch_id),
            remarks:        data.remarks,
            itemsArray:     data.items.map((row: any) => ({
              item_id:         row.item_id,
              item_name:       row.item_name,
              expense_type_id: Number(row.expense_type_id),
              qty:             Number(row.qty),
              unit:            row.unit,
              price:           Number(row.price),
            })),
            wht_percentage: data.wht_percentage,
            wht_amount:     data.wht_amount,
          });
          if (data.wht_percentage) setWithholdingTaxPercent(Number(data.wht_percentage));
        }
        setLoading("");
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenseTypes]);

  useEffect(() => {
    fetchExpenseTypes();
    fetchBranches();
    fetchSuppliers();
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Page title ───────────────────────────────────────────────────────────────
  const pageTitle = add ? "Add Purchase Request"
    : edit     ? "Edit Purchase Request"
    : approval ? "Approve Purchase Request"
    :            "View Purchase Request";

  const isDisabled = view || approval;

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <Layout className="page">
      <Helmet><title>Agua Lab ERP System</title></Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader title={pageTitle} withDateRangeFilter={false} withAddButton={false} withSearchBtn={false} />
          </Col>

          <div className="form-container">
            <Col span={24}>
              <Form form={form} onFinish={submitForm} layout="vertical">
                <Row className="w-full" gutter={[10, 10]}>

                  <Col xs={24} lg={8}>
                    <Form.Item label="Supplier Name" name="supplier_name"
                      rules={[{ required: true, whitespace: true, message: "This field is required." }]}>
                      <Input disabled={isDisabled} />
                    </Form.Item>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Form.Item label="Transaction Date" name="expense_date"
                      rules={[{ required: true, message: "This field is required." }]}>
                      <DatePicker disabled={isDisabled} className="w-full" format="MMMM D, YYYY" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={8}>
                    <Form.Item label="Branch" name="branch_id"
                      rules={[{ required: true, message: "This field is required." }]}>
                      <Select
                        placeholder="Select branch"
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        options={branches.map((b: any) => ({ label: b.name, value: Number(b.id) }))}
                        disabled={isDisabled}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Form.Item label="Remarks" name="remarks">
                      <Input.TextArea disabled={isDisabled} />
                    </Form.Item>
                  </Col>

                  <Row>
                    <span className="edit-label mb-2">
                      <label className="text-red-500 me-1">*</label>List of Items:
                    </span>
                    <Form.List name="itemsArray" initialValue={[{}]}>
                      {(fields, { add: addRow, remove }) => (
                        <>
                          <Col xs={24}>
                            <div className="overflow-auto max-h-[600px]">
                              <table className="w-full table-fixed border border-collapse min-w-[800px]">
                                <thead>
                                  <tr className="bg-primary text-white font-bold border">
                                    <th className="p-2 border w-[100px]">Item Name</th>
                                    <th className="p-2 border w-[80px]">Expense Type</th>
                                    <th className="p-2 border w-[50px]">Qty</th>
                                    <th className="p-2 border w-[50px]">Unit</th>
                                    <th className="p-2 border w-[60px]">Price</th>
                                    <th className="p-2 border w-[60px]">Total</th>
                                    <th className="p-2 border w-[40px] text-center">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {fields.map((field) => (
                                    <tr key={field.key} className="border bg-white">
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "item_name"]} rules={[{ required: true, message: "Required" }]} noStyle>
                                          <Input disabled={isDisabled} />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "expense_type_id"]} rules={[{ required: true, message: "Required" }]} noStyle>
                                          <Select
                                            className="w-full"
                                            placeholder="Select expense type"
                                            showSearch
                                            allowClear
                                            optionFilterProp="label"
                                            options={expenseTypes.map((t: any) => ({ label: t.name, value: Number(t.id) }))}
                                            disabled={isDisabled}
                                          />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "qty"]} rules={[{ required: true, message: "Required" }]} noStyle>
                                          <Input type="number" min={1} disabled={isDisabled} onWheel={(e) => (e.target as HTMLInputElement).blur()} />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "unit"]} rules={[{ required: true, message: "Required" }]} noStyle>
                                          <Input type="text" disabled={isDisabled} />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item name={[field.name, "price"]} rules={[{ required: true, message: "Required" }]} noStyle>
                                          <Input type="number" min={0} step="0.01" disabled={isDisabled} onWheel={(e) => (e.target as HTMLInputElement).blur()} />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item shouldUpdate noStyle>
                                          {() => {
                                            const qty    = form.getFieldValue(["itemsArray", field.name, "qty"]);
                                            const price  = form.getFieldValue(["itemsArray", field.name, "price"]);
                                            const total  = (Number(qty) || 0) * (Number(price) || 0);
                                            return <Input value={total.toFixed(2)} disabled />;
                                          }}
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border text-center">
                                        {fields.length > 1 && !isDisabled && (
                                          <Button onClick={() => remove(field.name)} icon={<Trash size={17} />} danger type="text" />
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Col>

                          <Col xs={24}>
                            {!isDisabled && (
                              <Row className="mt-2">
                                <Col span={17}>
                                  <Button type="primary" onClick={() => addRow()} icon={<Plus size={16} />}>Add Item</Button>
                                </Col>
                              </Row>
                            )}
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Subtotal:</strong></Col>
                              <Col span={5} className="text-start ps-5">₱ {subTotal.toFixed(2)}</Col>
                            </Row>
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Withholding Tax:</strong></Col>
                              <Col span={5} className="text-start ps-5">
                                <Radio.Group value={withholdingTaxPercent} onChange={(e) => setWithholdingTaxPercent(e.target.value)} disabled={isDisabled}>
                                  <Radio value={0}>0%</Radio>
                                  <Radio value={1}>1%</Radio>
                                  <Radio value={2}>2%</Radio>
                                </Radio.Group>
                              </Col>
                            </Row>
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Calculated Withholding Tax:</strong></Col>
                              <Col span={5} className="text-start ps-5">₱ {withholdingTaxAmount.toFixed(2)}</Col>
                            </Row>
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Grand Total:</strong></Col>
                              <Col span={5} className="text-start ps-5">₱ {formatNumbers(grandTotal)}</Col>
                            </Row>
                          </Col>
                        </>
                      )}
                    </Form.List>
                  </Row>

                  <Col xs={24} className="mt-5 pt-5">
                    <Form.Item
                      name="attachment"
                      valuePropName="fileList"
                      getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}
                    >
                      Attachment
                      <Dragger name="file" beforeUpload={handleBeforeUpload} accept=".pdf,image/*" disabled={isDisabled}>
                        <p className="ant-upload-drag-icon flex justify-center items-center text-3xl"><Inbox /></p>
                        <p className="ant-upload-text">Click or drag file to upload</p>
                        <p className="ant-upload-hint">Supports PDF, Image formats.</p>
                      </Dragger>
                      <p className="text-textColor pt-2">Note: Maximum file size is 2MB per file.</p>
                    </Form.Item>
                  </Col>

                  {/* ── Actions ──────────────────────────────── */}
                  <Col lg={24} className="mt-5 flex justify-end gap-3">
                    <Button onClick={() => { navigateTo("/purchase-request"); clearFields(); }}>
                      {isDisabled ? "Close" : "Cancel"}
                    </Button>

                    {approval && (
                      <Button
                        danger
                        loading={loading === "disapprove"}
                        onClick={() => { setDisapprovalReason(""); setShowModal("disapprove"); }}
                      >
                        Disapprove
                      </Button>
                    )}

                    {approval && (
                      <Button
                        type="primary"
                        loading={loading === "approve"}
                        onClick={() => setShowModal("approve")}
                      >
                        Approve
                      </Button>
                    )}

                    {(add || edit) && (
                      <Button type="primary" loading={loading === "submit"} htmlType="submit">
                        {add ? "Submit" : "Update"}
                      </Button>
                    )}
                  </Col>

                </Row>
              </Form>
            </Col>
          </div>
        </Row>
      </Layout>

      {/* ── Approve modal ─────────────────────────────────────────────────────── */}
      <ConfirmationModal
        title="Are you sure you want to approve this purchase request?"
        icon={<CheckCircle className="text-green-500 mt-5" size={60} />}
        showModal={showModal === "approve"}
        onCancel={() => setShowModal("")}
        footer={
          <div className="flex justify-center gap-3 mt-5">
            <Button onClick={() => setShowModal("")}>Cancel</Button>
            <Button type="primary" loading={loading === "approve"} onClick={approveRequest}>
              Approve
            </Button>
          </div>
        }
      />

      {/* ── Disapprove modal ──────────────────────────────────────────────────── */}
      <ConfirmationModal
        title="Are you sure you want to disapprove this purchase request?"
        icon={<XCircle className="text-red-500 mt-5" size={60} />}
        showModal={showModal === "disapprove"}
        onCancel={() => setShowModal("")}
        footer={
          <div className="flex flex-col gap-3 mt-5">
            <Input.TextArea
              rows={3}
              placeholder="Enter reason for disapproval..."
              value={disapprovalReason}
              onChange={(e) => setDisapprovalReason(e.target.value)}
            />
            <div className="flex justify-center gap-3">
              <Button onClick={() => setShowModal("")}>Cancel</Button>
              <Button danger loading={loading === "disapprove"} onClick={disapproveRequest}>
                Disapprove
              </Button>
            </div>
          </div>
        }
      />

      <PersistentFooter />
    </Layout>
  );
};

export default PurchaseRequestForm;